import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Star } from "lucide-react";

const CATEGORIES = [
  { key: "professionalism",       label: "Professionalism" },
  { key: "reliability",           label: "Reliability" },
  { key: "punctuality",           label: "Punctuality" },
  { key: "communication",         label: "Communication" },
  { key: "appearance",            label: "Appearance" },
  { key: "teamwork",              label: "Teamwork" },
  { key: "following_sops",        label: "Following SOPs" },
  { key: "overall_performance",   label: "Overall Performance" },
];

function StarRow({ value, onChange, testIdBase }) {
  return (
    <div className="flex gap-1" data-testid={testIdBase}>
      {[1, 2, 3, 4, 5].map((s) => (
        <button
          key={s}
          type="button"
          onClick={() => onChange(s)}
          className="p-0.5"
          data-testid={`${testIdBase}-${s}`}
          aria-label={`${s} star${s > 1 ? "s" : ""}`}
        >
          <Star className={`w-6 h-6 ${s <= value ? "text-amber-400 fill-amber-400" : "text-slate-300"}`} strokeWidth={1.5} />
        </button>
      ))}
    </div>
  );
}

/**
 * Full 8-category performance review form.
 * Props: booking { id, target_type, target_id }, onDone()
 */
export default function PerformanceReviewForm({ booking, onDone }) {
  const [ratings, setRatings] = useState(
    CATEGORIES.reduce((acc, c) => ({ ...acc, [c.key]: 5 }), {})
  );
  const [written, setWritten] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/reviews", {
        target_type: booking.target_type,
        target_id: booking.target_id,
        booking_id: booking.id,
        written,
        ...ratings,
      });
      toast.success("Performance review submitted");
      onDone && onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const avg = (Object.values(ratings).reduce((a, b) => a + b, 0) / CATEGORIES.length).toFixed(1);

  return (
    <div className="rounded-xl border border-white/10 bg-[#0A0A0A] p-6" data-testid="performance-review-form">
      <div className="flex flex-wrap justify-between items-start gap-3 mb-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-brand font-outfit font-bold">Performance Review</div>
          <h3 className="text-xl font-outfit font-semibold text-white mt-1">Rate this deployment</h3>
          <p className="text-sm text-white/60 font-manrope mt-1">Your review updates the professional&apos;s Job Match Score. Poor reviews trigger admin QA review.</p>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-white/60 font-outfit font-bold">Live avg</div>
          <div className="text-3xl font-outfit font-bold text-white">{avg}<span className="text-sm text-white/50">/5</span></div>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        {CATEGORIES.map((c) => (
          <div key={c.key} className="flex justify-between items-center border border-white/10 rounded-md px-3 py-2">
            <Label className="font-manrope text-sm">{c.label}</Label>
            <StarRow
              value={ratings[c.key]}
              onChange={(v) => setRatings({ ...ratings, [c.key]: v })}
              testIdBase={`review-${c.key}`}
            />
          </div>
        ))}
      </div>

      <div className="mt-4">
        <Label className="text-sm">Written feedback (optional)</Label>
        <Textarea
          rows={3}
          value={written}
          onChange={(e) => setWritten(e.target.value)}
          placeholder="Anything specific worth flagging or praising?"
          data-testid="review-written"
        />
      </div>

      <Button onClick={submit} disabled={busy} className="mt-4 bg-brand hover:bg-brand-hover text-white px-6 py-6 font-manrope font-semibold" data-testid="review-submit-btn">
        {busy ? "Submitting…" : "Submit Performance Review"}
      </Button>
    </div>
  );
}
